export const BRAND_ONBOARDING_ROUTE = '/onboarding/brand';
export const BRAND_ONBOARDING_SALES_STEP = 5;
export const BRAND_ONBOARDING_SALES_RETURN_TO = `${BRAND_ONBOARDING_ROUTE}?resume=sales`;

export interface OnboardingExternalRedirectContext {
  userId: string;
  route: string;
  step: number;
  brandId: string;
  brandStoreId?: string | null;
  returnTo: string;
  returnIntent: 'onboarding-sales';
  createdAt: string;
}

const stepStorageKey = (userId: string) => `kx:onboarding:brand:step:${userId}`;
const redirectStorageKey = (userId: string) => `kx:onboarding:brand:external:${userId}`;
const exitStorageKey = (userId: string) => `kx:onboarding:brand:exit:${userId}`;

// How long an explicit "I exited onboarding" flag suppresses auto-redirects
// back into /onboarding/brand. 30 minutes is enough to cover normal nav and
// a few refreshes, but expires so a stale flag can't permanently block a
// genuinely incomplete brand from being nudged back later.
const EXIT_FLAG_TTL_MS = 30 * 60 * 1000;

const writeBoth = (key: string, value: string) => {
  sessionStorage.setItem(key, value);
  localStorage.setItem(key, value);
};

const removeBoth = (key: string) => {
  sessionStorage.removeItem(key);
  localStorage.removeItem(key);
};

export const prepareOnboardingExternalRedirect = ({
  userId,
  step,
  brandId,
  brandStoreId,
  returnTo,
}: {
  userId: string;
  step: number;
  brandId: string;
  brandStoreId?: string | null;
  returnTo: string;
}) => {
  try {
    writeBoth(stepStorageKey(userId), String(step));
    writeBoth(redirectStorageKey(userId), JSON.stringify({
      userId,
      route: BRAND_ONBOARDING_ROUTE,
      step,
      brandId,
      brandStoreId: brandStoreId ?? null,
      returnTo,
      returnIntent: 'onboarding-sales',
      createdAt: new Date().toISOString(),
    } satisfies OnboardingExternalRedirectContext));
  } catch (err) {
    console.warn('[onboarding] external redirect persistence failed', err);
  }
};

export const loadOnboardingExternalRedirect = (userId: string): OnboardingExternalRedirectContext | null => {
  try {
    const raw = sessionStorage.getItem(redirectStorageKey(userId))
      ?? localStorage.getItem(redirectStorageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<OnboardingExternalRedirectContext>;
    if (parsed.userId === userId && parsed.returnIntent === 'onboarding-sales') {
      return parsed as OnboardingExternalRedirectContext;
    }
  } catch (err) {
    console.warn('[onboarding] external redirect persistence read failed', err);
  }
  return null;
};

export const clearOnboardingExternalRedirect = (userId: string) => {
  try {
    removeBoth(redirectStorageKey(userId));
  } catch (err) {
    console.warn('[onboarding] external redirect persistence clear failed', err);
  }
};

// ── Explicit exit flag ──────────────────────────────────────────────────
// Set when the user intentionally leaves onboarding (Skip for now,
// Go to dashboard, Command Center). RootRedirect / AuthCallback honor it
// so the user is not forced back into the wizard on the next render.
export const markOnboardingExited = (userId: string) => {
  try {
    writeBoth(exitStorageKey(userId), String(Date.now()));
  } catch (err) {
    console.warn('[onboarding] exit flag write failed', err);
  }
};

export const hasFreshOnboardingExit = (userId: string): boolean => {
  try {
    const raw = sessionStorage.getItem(exitStorageKey(userId))
      ?? localStorage.getItem(exitStorageKey(userId));
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!Number.isFinite(ts)) return false;
    if (Date.now() - ts > EXIT_FLAG_TTL_MS) {
      removeBoth(exitStorageKey(userId));
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

export const clearOnboardingExitFlag = (userId: string) => {
  try {
    removeBoth(exitStorageKey(userId));
  } catch { /* ignore */ }
};

// Clear ALL onboarding persistence (step + external + exit). Used by
// the Skip / Go to dashboard handlers and by FinishStep on completion.
export const clearAllOnboardingPersistence = (userId: string) => {
  try {
    removeBoth(stepStorageKey(userId));
    removeBoth(redirectStorageKey(userId));
  } catch { /* ignore */ }
};
